import {
  MANIFEST_FILE,
  SongPackLoader,
  decodeJson,
  isSongPackError,
  loadSongPackParts,
  parseManifest,
  readMvFileReference,
  readWavDurationSeconds,
  type LoadedSongPack,
} from '../songpack/index.js';
import { byNewest, describe, entryBytes, type SongEntry, type SongSource } from './SongEntry.js';
import { IndexedDbSongRepository } from './IndexedDbSongRepository.js';
import {
  attachVideo,
  splitPack,
  videoEntryNameFor,
  type AssetMap,
} from './SongPackAssets.js';
import {
  ASSET_NAMES,
  entryNameFor,
  measure,
  REQUIRED_ASSETS,
  type AssetName,
  type SongRepository,
} from './SongRepository.js';
import { writeSongPackArchive, type WriteReport } from './SongPackWriter.js';

/**
 * The library: what songs exist, and how to get one out.
 *
 * Holds the index in memory — it is small, it is read once, and Song Select
 * redraws from it constantly. Everything else stays in the repository until
 * something asks for it by name.
 *
 * The shape of the promise this makes to the rest of the runtime:
 *
 *   launch  ->  refresh()          index only; nothing is decoded
 *   select  ->  entries            a list, drawn from memory
 *   play    ->  open(packId)       that song's assets, validated again
 *   export  ->  exportArchive()    a rebuilt .zip from stored bytes
 *
 * A song is never half-present. `add` writes the index record and every asset in
 * one transaction, so a failure leaves the library exactly as it was.
 */

export type AddOutcome =
  | { readonly status: 'added'; readonly entry: SongEntry }
  | { readonly status: 'replaced'; readonly entry: SongEntry }
  /** A song with this `packId` is already stored. Nothing was written. */
  | { readonly status: 'duplicate'; readonly existing: SongEntry };

export interface AddOptions {
  readonly source: SongSource;
  /** Length in seconds, when the caller already measured it. */
  readonly durationSeconds?: number;
  /**
   * Whether to overwrite a song with the same `packId`.
   *
   * Defaults to false, and the default is the point: importing a pack whose id
   * collides with one already stored returns `duplicate` and writes nothing, so
   * the decision to destroy the existing song is always the player's.
   */
  readonly replace?: boolean;
  /** A music video to bundle, for packs that did not arrive with one. */
  readonly video?: { readonly blob: Blob; readonly fileName: string } | undefined;
}

/** A song, taken out of storage and ready for the runtime. */
export interface OpenedSong {
  readonly entry: SongEntry;
  readonly pack: LoadedSongPack;
  /**
   * The bundled music video, still a `Blob`.
   *
   * Never read into memory here. The caller turns it into an object URL, which
   * is a handle to the browser's own copy — a 200 MB MV costs a string.
   */
  readonly video: Blob | null;
}

export interface StorageReport {
  readonly songs: number;
  readonly storedBytes: number;
  /** Browser's own estimate, when it offers one. Advisory, never a guarantee. */
  readonly usageBytes: number | null;
  readonly quotaBytes: number | null;
}

export class SongLibrary {
  private readonly repository: SongRepository;
  private index: SongEntry[] = [];
  private loaded = false;

  constructor(repository: SongRepository = new IndexedDbSongRepository()) {
    this.repository = repository;
  }

  /** Every song, newest first. Empty until `refresh()` has run. */
  get entries(): readonly SongEntry[] {
    return this.index;
  }

  get ready(): boolean {
    return this.loaded;
  }

  find(packId: string): SongEntry | null {
    return this.index.find((entry) => entry.packId === packId) ?? null;
  }

  /** Reads the index. No assets are touched, so this stays fast with big songs. */
  async refresh(): Promise<readonly SongEntry[]> {
    this.index = (await this.repository.list()).sort(byNewest);
    this.loaded = true;
    return this.index;
  }

  /**
   * Stores a Song Pack from its archive bytes.
   *
   * The archive is read by `SongPackLoader` first, so nothing reaches storage
   * that this runtime could not later play. That is also what makes the library
   * a compatibility check rather than a filing cabinet.
   */
  async addArchive(archive: ArrayBuffer | Uint8Array, options: AddOptions): Promise<AddOutcome> {
    const bytes = archive instanceof ArrayBuffer ? new Uint8Array(archive) : archive;
    // `skip`, not `include`. The loader still validates a bundled video's
    // header, and `splitPack` builds its `Blob` from a view over the archive —
    // so a 200 MB MV is never inflated into the JavaScript heap on the way in.
    const pack = SongPackLoader.loadSync(bytes, { video: 'skip' });
    let assets = splitPack(bytes, pack);
    if (options.video !== undefined) {
      assets = await attachVideo(assets, options.video.blob, videoEntryNameFor(options.video.fileName));
    }
    return this.store(pack, assets, options);
  }

  /**
   * Stores a pack whose assets the caller already holds.
   *
   * The CREATE path, where the archive came from the chart engine and the video
   * is the `File` the player picked — which must not be read into memory just to
   * be written straight back out.
   */
  async addAssets(
    pack: LoadedSongPack,
    assets: AssetMap,
    options: AddOptions,
  ): Promise<AddOutcome> {
    return this.store(pack, assets, options);
  }

  private async store(
    pack: LoadedSongPack,
    assets: AssetMap,
    options: AddOptions,
  ): Promise<AddOutcome> {
    for (const required of REQUIRED_ASSETS) {
      if (!assets.has(required)) {
        throw new Error(`The pack is missing its ${required}, so it was not stored.`);
      }
    }

    // Read back out of the assets rather than taken from the pack that was
    // handed in. Attaching a video rewrites the manifest, so the pack object and
    // the bytes about to be stored can differ — and the index must describe what
    // is stored, not what was proposed. This is what makes "the manifest is the
    // truth and the entry is a projection of it" an invariant rather than a
    // comment.
    const manifestBytes = new Uint8Array(await (assets.get('manifest') as Blob).arrayBuffer());
    const manifest = parseManifest(decodeJson(manifestBytes, MANIFEST_FILE));
    const stored: LoadedSongPack = {
      ...pack,
      manifest,
      videoEntry: assets.has('video') ? (readMvFileReference(manifest) ?? null) : null,
    };

    const packId = manifest.packId;
    const existing = await this.repository.get(packId);
    if (existing !== null && options.replace !== true) {
      return { status: 'duplicate', existing };
    }

    // Measured from the RIFF header when the caller did not already know it, so
    // an imported song shows a length without anything being decoded.
    const durationSeconds =
      options.durationSeconds ?? readWavDurationSeconds(pack.music);

    // A replacement keeps the original creation time; it is the same song.
    const entry = describe(stored, {
      source: options.source,
      durationSeconds,
      ...(existing === null ? {} : { createdAt: existing.createdAt }),
      bytes: measure(assets),
    });

    await this.repository.put(entry, assets);
    this.index = [entry, ...this.index.filter((value) => value.packId !== packId)].sort(byNewest);
    return existing === null
      ? { status: 'added', entry }
      : { status: 'replaced', entry };
  }

  /**
   * Reads one song back out and validates it again.
   *
   * Revalidating what we ourselves wrote is not paranoia about the database. It
   * is that the alternative — trusting stored bytes — means a corrupt or
   * partially written song fails somewhere deep in the simulation instead of
   * here, with a name attached.
   */
  async open(packId: string): Promise<OpenedSong> {
    const entry = this.find(packId) ?? (await this.repository.get(packId));
    if (entry === null) throw new Error(`No song with id ${packId} is in the library.`);

    const wanted: AssetName[] = ['manifest', 'easy', 'normal', 'hard', 'music'];
    if (entry.hasCover) wanted.push('cover');
    const assets = await this.repository.assets(packId, wanted);

    const parts = {
      manifest: await bytesOf(assets, 'manifest'),
      easy: await bytesOf(assets, 'easy'),
      normal: await bytesOf(assets, 'normal'),
      hard: await bytesOf(assets, 'hard'),
      music: await bytesOf(assets, 'music'),
      cover: entry.hasCover ? await bytesOf(assets, 'cover') : null,
      videoEntry: entry.hasVideo ? entry.mvReference : null,
    };

    let pack: LoadedSongPack;
    try {
      pack = loadSongPackParts(parts);
    } catch (cause) {
      throw new Error(
        `"${entry.displayName}" is stored but unreadable ` +
          `(${isSongPackError(cause) ? cause.code : String(cause)}).`,
        { cause },
      );
    }

    // Fetched last and separately, so a song with no video never queries for one
    // and a song with one never inflates it.
    const video = entry.hasVideo ? await this.repository.asset(packId, 'video') : null;
    return { entry, pack, video };
  }

  /**
   * The cover of one song, without opening the rest of it.
   *
   * Song Select draws the cover of whichever song is highlighted, so this has to
   * be reachable on its own — reading a 20 MB `music.wav` to show a thumbnail is
   * how a library of fifty songs becomes unusable.
   */
  async openCover(packId: string): Promise<Blob | null> {
    return this.repository.asset(packId, 'cover');
  }

  /** Rebuilds a `.zip` from stored assets, video included. */
  async exportArchive(packId: string): Promise<{ blob: Blob; fileName: string; report: WriteReport }> {
    const entry = this.find(packId) ?? (await this.repository.get(packId));
    if (entry === null) throw new Error(`No song with id ${packId} is in the library.`);
    const assets = await this.repository.assets(packId, ASSET_NAMES);

    // Ordered as the Python engine writes them, with the video last. Nothing
    // depends on the order — every reader indexes by name — but a pack that
    // opens the same way in a file manager is easier to compare.
    const order: AssetName[] = ['manifest', 'music', 'cover', 'easy', 'normal', 'hard', 'video'];
    const written = await writeSongPackArchive(
      order
        .filter((name) => assets.has(name))
        .map((name) => ({
          name: entryNameFor(name, entry),
          blob: assets.get(name) as Blob,
          // The video is the only asset already compressed; deflating an MP4
          // costs CPU and makes it bigger.
          compress: name !== 'video',
        })),
    );
    return { blob: written.blob, fileName: `${entry.packId}.zip`, report: written.report };
  }

  /** Removes a song and everything it owns. */
  async remove(packId: string): Promise<boolean> {
    const removed = await this.repository.remove(packId);
    this.index = this.index.filter((entry) => entry.packId !== packId);
    return removed;
  }

  /**
   * What the library occupies.
   *
   * `storedBytes` is this library's own accounting and is exact. `usageBytes`
   * and `quotaBytes` come from `navigator.storage.estimate()`, which is
   * deliberately imprecise and covers the whole origin — it is shown as a hint,
   * never treated as a budget to fill.
   */
  async storage(): Promise<StorageReport> {
    let storedBytes = 0;
    for (const entry of this.index) storedBytes += entryBytes(entry);
    let usageBytes: number | null = null;
    let quotaBytes: number | null = null;
    try {
      const estimate = await navigator.storage?.estimate?.();
      usageBytes = estimate?.usage ?? null;
      quotaBytes = estimate?.quota ?? null;
    } catch {
      // Not offered, or refused. The library's own total still stands.
    }
    return { songs: this.index.length, storedBytes, usageBytes, quotaBytes };
  }

  close(): void {
    this.repository.close();
  }
}

async function bytesOf(assets: ReadonlyMap<AssetName, Blob>, name: AssetName): Promise<Uint8Array> {
  const blob = assets.get(name);
  if (blob === undefined) throw new Error(`The stored song is missing its ${name}.`);
  return new Uint8Array(await blob.arrayBuffer());
}
